import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Button, Tag } from "../../../../../design-system";
import { MediaProject } from "../../../../../types/MediaProject";
import { VideoLibraryItem } from "../../../../../types/Community";
import {
  ScheduledPost,
  SchedulePlatform,
} from "../../../../../database/community/scheduledPosts";
import { ALL_PLATFORMS, platformLabel } from "../../publish/SchedulePostDialog";
import { Muted } from "../shared";
import MediaPickerModal from "./MediaPickerModal";

// One draft, full width, everything editable before the approve decision.

const CardRoot = styled.div`
  display: grid;
  grid-template-columns: minmax(220px, 320px) minmax(0, 1fr);
  gap: var(--space-4);
  padding: var(--space-4);
  border-radius: var(--radius-xl);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  box-shadow: var(--shadow-md);

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const PreviewBox = styled.div`
  position: relative;
  border-radius: var(--radius-lg);
  overflow: hidden;
  background: var(--color-surface-subtle);
  aspect-ratio: 4 / 5;
  display: flex;
  align-items: center;
  justify-content: center;

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const MediaColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`;

const SlideDots = styled.div`
  position: absolute;
  bottom: 8px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 5px;
`;

const SlideDot = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  cursor: pointer;
  background: ${(p) => (p.$active ? "#fff" : "rgba(255, 255, 255, 0.45)")};
  box-shadow: 0 0 3px rgba(0, 0, 0, 0.4);
`;

const NoPreview = styled.div`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  color: var(--color-text-tertiary);
  padding: var(--space-4);
  text-align: center;
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
`;

const FieldLabel = styled.div`
  font-family: var(--font-sans);
  font-size: 10px;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-tertiary);
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const CaptionInput = styled.textarea`
  appearance: none;
  width: 100%;
  min-height: 120px;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-base);
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }
`;

const PlatformChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const PlatformChip = styled.button<{ $on: boolean }>`
  appearance: none;
  border: 1px solid
    ${(p) => (p.$on ? "var(--color-accent)" : "var(--color-border-subtle)")};
  background: ${(p) => (p.$on ? "var(--color-accent-subtle)" : "var(--color-surface)")};
  color: ${(p) => (p.$on ? "var(--color-accent-text)" : "var(--color-text-secondary)")};
  font-family: var(--font-sans);
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  padding: 4px 10px;
  border-radius: var(--radius-pill);
  cursor: pointer;
  text-transform: capitalize;
`;

const WhenInput = styled.input`
  appearance: none;
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  padding: 6px 10px;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  max-width: 240px;

  &:focus {
    outline: none;
    border-color: var(--color-accent);
    box-shadow: 0 0 0 3px var(--color-focus-ring);
  }
`;

const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  margin-top: auto;
`;

const isoToLocalInput = (iso: string | null): string => {
  const d = iso ? new Date(iso) : new Date(Date.now() + 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

export type ReviewDecision = {
  caption: string;
  dueAtIso: string;
  platforms: SchedulePlatform[];
  changed: boolean;
};

export type ReviewCardProps = {
  post: ScheduledPost;
  project?: MediaProject;
  communityId?: string;
  busy: boolean;
  firstParty: Partial<Record<SchedulePlatform, boolean>>;
  onApprove: (decision: ReviewDecision) => void;
  onReject: () => void;
  onSkip: () => void;
  /** Replace this post's media with a library item (persists immediately). */
  onReplaceMedia?: (item: VideoLibraryItem) => void;
  /** Registers the caption textarea so the queue's E shortcut can focus it. */
  captionRef?: React.RefObject<HTMLTextAreaElement>;
};

export const ReviewCard: React.FC<ReviewCardProps> = ({
  post,
  project,
  communityId,
  busy,
  firstParty,
  onApprove,
  onReject,
  onSkip,
  onReplaceMedia,
  captionRef,
}) => {
  const [caption, setCaption] = useState(post.caption);
  const [when, setWhen] = useState(isoToLocalInput(post.dueAt));
  const [platforms, setPlatforms] = useState<SchedulePlatform[]>(
    post.platforms as SchedulePlatform[]
  );
  const [slideIndex, setSlideIndex] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const localRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = captionRef || localRef;

  // Reset edit state when the card shows a different draft.
  useEffect(() => {
    setCaption(post.caption);
    setWhen(isoToLocalInput(post.dueAt));
    setPlatforms(post.platforms as SchedulePlatform[]);
    setSlideIndex(0);
  }, [post.id, post.caption, post.dueAt, post.platforms]);

  // Media can come from a linked project OR from mediaUrls on the post itself
  // (posts scheduled from raw URLs have no project). Resolve both so a draft
  // always shows its media, not just project-backed ones.
  const videoExt = /\.(mp4|mov|webm|m4v)(\?|$)/i;
  const rawMedia = post.mediaUrls || [];
  const projectSlideUrls =
    project?.kind === "carousel"
      ? (project.slides || []).map((s) => s.mediaUrl).filter(Boolean)
      : [];
  const imageUrls = projectSlideUrls.length
    ? projectSlideUrls
    : rawMedia.filter((u) => !videoExt.test(u));
  const videoUrl =
    (project?.kind === "reel" ? project?.outputUrl : undefined) ||
    (!project && rawMedia.length === 1 && videoExt.test(rawMedia[0])
      ? rawMedia[0]
      : undefined);
  const isCarousel = imageUrls.length > 0;

  const togglePlatform = (p: SchedulePlatform) =>
    setPlatforms((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
    );

  const buildDecision = (): ReviewDecision => {
    const dueAtIso = new Date(when).toISOString();
    const changed =
      caption !== post.caption ||
      dueAtIso !== post.dueAt ||
      platforms.join(",") !== post.platforms.join(",");
    return { caption, dueAtIso, platforms, changed };
  };

  return (
    <CardRoot>
      <MediaColumn>
        <PreviewBox>
          {isCarousel ? (
            <>
              <img
                src={imageUrls[Math.min(slideIndex, imageUrls.length - 1)]}
                alt=""
              />
              {imageUrls.length > 1 && (
                <SlideDots>
                  {imageUrls.map((_, i) => (
                    <SlideDot
                      key={i}
                      type="button"
                      aria-label={`Slide ${i + 1}`}
                      $active={i === slideIndex}
                      onClick={() => setSlideIndex(i)}
                    />
                  ))}
                </SlideDots>
              )}
            </>
          ) : videoUrl ? (
            <video src={videoUrl} controls preload="metadata" playsInline />
          ) : project?.thumbnailUrl ? (
            <img src={project.thumbnailUrl} alt="" />
          ) : (
            <NoPreview>
              No media preview. This draft posts with its caption only.
            </NoPreview>
          )}
        </PreviewBox>
        {onReplaceMedia && (
          <Button
            variant="secondary"
            size="sm"
            disabled={busy}
            onClick={() => setPickerOpen(true)}
          >
            Replace image for {platformLabel(post.platforms[0] || "this post")}
          </Button>
        )}
      </MediaColumn>

      <Fields>
        <div>
          <FieldLabel>
            {project?.kind === "reel" ? "Reel" : "Carousel"}
            {project?.title ? ` · ${project.title}` : ""}
          </FieldLabel>
        </div>

        <div>
          <FieldLabel>Caption</FieldLabel>
          <CaptionInput
            ref={textareaRef}
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Write the caption this post goes out with."
          />
        </div>

        <div>
          <FieldLabel>Platforms</FieldLabel>
          <PlatformChips>
            {ALL_PLATFORMS.map((p) => (
              <PlatformChip
                key={p}
                type="button"
                $on={platforms.includes(p)}
                onClick={() => togglePlatform(p)}
                title={
                  firstParty[p]
                    ? "Publishes via the connected account"
                    : "Publishes via the cross-post service"
                }
              >
                {platformLabel(p)}
                {firstParty[p] ? " ✓" : ""}
              </PlatformChip>
            ))}
          </PlatformChips>
        </div>

        <div>
          <FieldLabel>Goes out</FieldLabel>
          <WhenInput
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
          />
        </div>

        <ActionsRow>
          <Button
            variant="primary"
            size="md"
            isLoading={busy}
            disabled={busy || platforms.length === 0}
            onClick={() => onApprove(buildDecision())}
          >
            Approve
          </Button>
          <Button variant="secondary" size="md" disabled={busy} onClick={onSkip}>
            Skip
          </Button>
          <Button variant="danger" size="md" disabled={busy} onClick={onReject}>
            Reject
          </Button>
          <Muted>Keys: A approve, X reject, arrows move, E edit caption</Muted>
          {post.status !== "draft" && (
            <Tag variant="info" size="sm">
              {post.status}
            </Tag>
          )}
        </ActionsRow>
      </Fields>

      {pickerOpen && onReplaceMedia && (
        <MediaPickerModal
          communityId={communityId}
          onPick={(item) => {
            onReplaceMedia(item);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </CardRoot>
  );
};

export default ReviewCard;
